import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import userSelectors from '@redux/user/selectors';

import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { useGetProductFullView } from 'hooks';

import { checkIsDollarProduct } from 'pages/qa-spec/views/qa-spec-detail/utils';

import * as qasSpecServices from 'services/qas-require-field';
export const requiredFieldsMap = {
  //* header
  'qaSpecification.category1Name': 'qaSpecification.category1',
  'qaSpecification.category2Name': 'qaSpecification.category2',
  'qaSpecification.category3Name': 'qaSpecification.category3',
  'qaSpecification.category4Name': 'qaSpecification.category4',
  'eCommerce.smartLabelTemplate': 'qaSpecification.smartLabelTemplate',
  //* product spec tab
  'tdcProperties.glutenFreeYN':
    'qaSpecification.qaSpecProductSpecification.glutenFreeClaim',
  'qaSpecification.safeHandlingInstructions':
    'qaSpecification.qaSpecProductSpecification.safeHandlingInstructions',
  targetMarket: 'qaSpecification.qaSpecProductSpecification.targetMarket',
  'placeOfItemActivity.placeOfProductActivity.countryOfOrigin.countryCode':
    'qaSpecification.qaSpecProductSpecification.countryOfOrigin.countryCode',
  'placeOfItemActivity.placeOfProductActivity.countryOfOrigin.countrySubdivisionCode':
    'qaSpecification.qaSpecProductSpecification.countryOfOrigin.countrySubdivisionCode',
  'tdcProperties.unit_MinimumShelfLife':
    'qaSpecification.qaSpecProductSpecification.minimumShelfLife',
  'dProperties.bpSKULabelling.leftLanguage.qualityGuarantee':
    'qaSpecification.qaSpecProductSpecification.qualityGuarantee',
  'tdcProperties.unit_MaximumShelfLife':
    'qaSpecification.qaSpecProductSpecification.maximumShelfLife',
  'tdcProperties.nonGmoGmoStatement':
    'qaSpecification.qaSpecProductSpecification.nonGmoStatement',
  'tdcProperties.case_TemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeHigh',
  'tdcProperties.case_TemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeLow',
  'tdcProperties.case_ShippingTemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureHigh',
  'tdcProperties.case_ShippingTemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureLow',
  'nonfoodIngredient.nonfoodIngredientStatement.value':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.value',
  'nonfoodIngredient.nonfoodIngredientStatement.languageCode':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.languageCode',
  'nonfoodIngredient.nonfoodIngredientStatement.codeListVersion':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.codeListVersion',
  'nonfoodIngredient.nonfoodIngredientStatement.formattingPattern':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.formattingPattern',
  'nonfoodIngredient.nonfoodIngredientStatement.sequenceNumber':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.sequenceNumber',
  'certification.kosherCertification':
    'qaSpecification.qaSpecProductSpecification.kosherCertifier',
  'certification.organicCertifier':
    'qaSpecification.qaSpecProductSpecification.organicCertifier',
  'certification.halalCertifier':
    'qaSpecification.qaSpecProductSpecification.halalCertifier',
  'certification.nonGMOCertifier':
    'qaSpecification.qaSpecProductSpecification.nonGMOCertifier',
  'certification.iSOCertifier':
    'qaSpecification.qaSpecProductSpecification.iSOCertifier',
  'certification.bRCCertifier':
    'qaSpecification.qaSpecProductSpecification.bRCCertifier',
  'certification.iFSCertifier':
    'qaSpecification.qaSpecProductSpecification.iFSCertifier',
  //* labeling tab
  'consumerInstructions.consumerInstructions.consumerStorageInstructions.value':
    'qaSpecification.qaSpecPackaging.storageInstructions',
  'tradeItemHandling.tradeItemHandlingInformation.handlingInstructionsDescription.value':
    'qaSpecification.qaSpecPackaging.handlingInstructions',
  'consumerInstructions.consumerInstructions.consumerUsageInstructions.value':
    'qaSpecification.qaSpecPackaging.usage',
  'sustainability.sustainabilityInformation.totalRecyclableContentPercentage':
    'qaSpecification.qaSpecPackaging.totalRecyclableContentPercentage',
  'sustainability.sustainabilityInformation.postConsumerRecycledContentPercentage':
    'qaSpecification.qaSpecPackaging.postConsumerRecycledContentPercentage',
  'packagingMarking.packagingMarking.consumerWarningInformation.consumerWarningDescription.value':
    'qaSpecification.qaSpecPackaging.warningStatement',
  productName: 'qaSpecification.qaSpecPackaging.productName',
  upc12: 'qaSpecification.qaSpecPackaging.upc12',
  primarySize: 'qaSpecification.qaSpecPackaging.size',
  primarySizeUOM: 'qaSpecification.qaSpecPackaging.uom',
  'dProperties.bpSKULabelling.leftLanguage.frontPanelSecondaryDescrip':
    'qaSpecification.qaSpecPackaging.secondaryDescriptor',
  'dProperties.bpSKULabelling.leftLanguage.tertiaryDescriptor':
    'qaSpecification.qaSpecPackaging.tertiaryDescriptor',
  'packagingInformation.packaging.packagingMaterial.packagingMaterialTypeCode':
    'qaSpecification.qaSpecPackaging.packagingMaterial',
  'packagingInformation.packaging.packagingRecyclingSchemeCode':
    'qaSpecification.qaSpecPackaging.recyclableTypes',
  'packagingInformation.packaging.packagingTypeCode':
    'qaSpecification.qaSpecPackaging.packagingType',
  'eCommerce.rivirMarketingDescription':
    'qaSpecification.qaSpecPackaging.romanceCopy',
  'foodAndBeveragePreparationServing.preparationServing.recipe.value':
    'qaSpecification.qaSpecPackaging.recipe',

  //* ingredient tab
  'foodAndBeverageIngredient.ingredientStatement.value':
    'qaSpecification.qaSpecIngredients.ingredientStatement',
  'dProperties.ingredientsProductClaims':
    'qaSpecification.qaSpecIngredients.ingredientClaims',
  //* fresh meat
  'foodAndBeverageIngredient.foodAndBeverageIngredient.ingredientFarmingProcessing.irradiatedCode':
    'qaSpecification.qaSpecFreshMeat.irradiated',
  //* fresh produce
  'foodAndBeveragePreparationServing.preparationServing.preparationInstructions.value':
    'qaSpecification.qaSpecFreshProduce.cookingInstructions',
  //* fresh sea food
  'organismClassification.organismClassification.genus':
    'qaSpecification.qaSpecFreshSeaFood.genus',
  'organismClassification.organismClassification.species':
    'qaSpecification.qaSpecFreshSeaFood.species',
  //* component
  'dProperties.componentsProductClaims':
    'qaSpecification.bpComponents.productClaims',
  //* Pet food
  'tdcProperties.productWarningStatement':
    'qaSpecification.qaSpecPetFood.warnings',
  //* Fresh Deli
  'qaSpecification.typeOfCutOrTrimSpec':
    'qaSpecification.qaSpecFreshMeat.typeOfCutOrTrimSpec',
  //* Contact
  'qaSpecification.qaSpecContacts.productionFacilityNo1.name':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityName',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.addressLine1':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityAddressLine1',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.addressLine2':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityAddressLine2',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManager':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerName',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityPlantManagerPhone',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContact':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaName',
  'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo1.facilityQaPhone',

  'qaSpecification.qaSpecContacts.productionFacilityNo2.name':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityName',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.addressLine1':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityAddressLine1',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.addressLine2':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityAddressLine2',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManagerContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManagerEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManager':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManagerName',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManagerContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityPlantManagerPhone',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaContact':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaName',
  'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo2.facilityQaPhone',

  'qaSpecification.qaSpecContacts.productionFacilityNo3.name':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityName',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.addressLine1':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityAddressLine1',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.addressLine2':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityAddressLine2',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManagerContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManagerEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManager':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManagerName',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManagerContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityPlantManagerPhone',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaContactEmail':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaEmail',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaContact':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaName',
  'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaContactPhone':
    'qaSpecification.qaSpecContacts.productionFacilityNo3.facilityQaPhone',
  //* fact panel
  'qaSpecification.factsPanel.supplementLabel.servingSize.servingSizeDescription':
    'qaSpecification.factsPanel.supplementLabel.servingSizeDescription',
  'qaSpecification.factsPanel.drugFactsLabel.activeIngredients.dosageUnit':
    'qaSpecification.factsPanel.drugFactsLabel.dosageUnit',
};

export const getGetQaSpecRequiredFieldsKeys = (params, userId) => {
  return ['get-qa-spec-required-fields', params, userId];
};

export const useGetQaSpecRequiredFields = (props) => {
  const { params, fieldPath, ...options } = props;

  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const isDollarProduct = checkIsDollarProduct(params?.ownerId);

  const getGetQaSpecRequiredFields = async ({ queryKey }) => {
    const params = queryKey[1];

    const response = await qasSpecServices.getQaSpecRequiredFieldForProduct(
      params
    );

    if (!response.isSuccess || !response?.data?.requiredFields) return null;

    //* map data
    const requiredFields = response.data.requiredFields
      .filter((field) => {
        //* filter out product contain field when product does not belong to dollar general

        //* if product  belongs to dollar general, accept all fields
        if (isDollarProduct) return true;

        //* if not, accept fields that is not product contain fields
        const isProductContainField = field.includes(
          'qaSpecification.qaSpecProductContains'
        );

        return !isProductContainField;
      })
      .map((field) => {
        //* some fields  in Product Spec tab have difference fieldName with required fields, format them to standard field full path for logic flow
        const formattedFields = requiredFieldsMap[field];

        const finalField = formattedFields ? formattedFields : field;

        const downcaseFormattedField = finalField.toLowerCase();

        return downcaseFormattedField;
      });

    return requiredFields;
  };

  return useQuery({
    queryKey: getGetQaSpecRequiredFieldsKeys(params, { userId: userInfo?.id }),
    queryFn: getGetQaSpecRequiredFields,
    ...options,
  });
};

export const useGetQaSpecRequiredFieldsInQaSpecFullView = (props) => {
  const { productId } = props;

  const { productFull } = useGetProductFullView({
    productId,
  });

  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: !!productId,
  });

  const productType = qaSpecDataHeader?.type ?? null;

  return useGetQaSpecRequiredFields({
    params: {
      ownerId: productFull?.ownerId,
      productType,
    },
    enabled: !!productFull && !!productType && !!productFull?.ownerId,
  });
};
